/**
 * The hand-drawn map SVG renderer (Big Phase 1.5 UI). A PURE React component
 * that paints the deterministic {@link buildDrawPlan} output as lightweight SVG:
 *   - constructed shapes → `<polygon>`, organic shapes → `<path>` (seeded hull),
 *   - edges → `<line>` (straight) / `<path>` (Bezier for curved corridors),
 *   - barrier glyphs on edges, FOUND trap/secret markers on nodes,
 *   - the per-character darkness mask (dimmed `masked` nodes),
 *   - the "you are here" highlight on the party's current node.
 *
 * BOUNDARY: this renders EXACTLY the server's `filterForPlayer` payload — the
 * fog-of-war trust boundary already stripped undiscovered nodes, hidden edges,
 * trap DCs, and unfound secrets SERVER-SIDE. The renderer cannot show what it
 * never received; it has no notion of hidden geometry (R4/R11.6). It is also a
 * navigation/exploration view, never a combat grid (the middle-path boundary).
 *
 * Zoom/pan is applied by the parent {@link MapViewport} via the `transform`
 * prop (an SVG transform string); this component stays a pure painter so its
 * output is deterministic for a given payload + seed and unit-testable in jsdom.
 */

import { useMemo } from "react";
import type { SpatialMapGraph } from "@rufus/shared-types";

import { buildDrawPlan, type DrawCommand, type RenderPayload } from "./draw-plan.js";
import { SeededRandom } from "./seeded-random.js";

export interface MapCanvasProps {
  /** The sanitized, player-safe map payload for one Z-slice (server-filtered). */
  readonly map: SpatialMapGraph;
  /** The party's current node id, highlighted + labelled "you are here" (R-UI). */
  readonly currentNodeId?: string;
  /** An SVG `transform` string from the zoom/pan viewport (identity when absent). */
  readonly transform?: string;
  /** Accessible label for the SVG region. */
  readonly ariaLabel?: string;
  /** Extra padding (in map feet) around the content bounds. */
  readonly padFt?: number;
}

/** A point in SVG user space (map feet; SVG Y grows DOWN so we flip map-north). */
type Pt = readonly [number, number];

/** Map a point to the SVG's Y-down space (map north = up ⇒ negate y). */
function toSvg([x, y]: Pt): Pt {
  return [x, -y];
}

/** Serialize points to an SVG `points`/path string, flipped to Y-down space. */
function pointsAttr(points: readonly Pt[]): string {
  return points.map((p) => toSvg(p).join(",")).join(" ");
}

/** A quadratic Bezier path `M a Q ctrl b` for a curved corridor's 3 control points. */
function bezierPath(points: readonly Pt[]): string {
  const [a, ctrl, b] = points.map(toSvg);
  return `M ${a![0]} ${a![1]} Q ${ctrl![0]} ${ctrl![1]} ${b![0]} ${b![1]}`;
}

/** One stair tread: a transverse segment across the landing, with a draw opacity. */
interface StairTread {
  readonly a: Pt;
  readonly b: Pt;
  readonly opacity: number;
}

/**
 * Evenly-spaced STAIR TREADS across a vertical landing's spine (feet space). Each
 * tread is a short segment PERPENDICULAR to the landing axis, so the stamp reads
 * as "steps", not a corridor. Direction shades them: DOWN treads taper + darken
 * toward the far (descending-into-shadow) end; UP treads stay full-strength near
 * the room and fade toward the far (ascending-into-the-ceiling) end. The caller
 * flips to SVG space via `toSvg`.
 */
function stairTreads(
  spine: readonly Pt[],
  halfWidth: number,
  vertical: "up" | "down",
): readonly StairTread[] {
  const a = spine[0];
  const b = spine[spine.length - 1];
  if (!a || !b) return [];
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy) || 1;
  // Unit along the landing axis and its perpendicular (the tread direction).
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  const COUNT = 5;
  const treads: StairTread[] = [];
  for (let k = 1; k <= COUNT; k++) {
    const t = k / (COUNT + 1); // 0..1 along the spine (skip the exact ends)
    const cx = a[0] + dx * t;
    const cy = a[1] + dy * t;
    // DOWN: treads shrink toward the far end (receding). UP: full width.
    const wScale = vertical === "down" ? 1 - 0.45 * t : 1;
    const w = halfWidth * 0.8 * wScale;
    // DOWN: darker toward the far end. UP: fades out toward the far end.
    const opacity = vertical === "down" ? 0.55 + 0.45 * t : 0.9 - 0.55 * t;
    treads.push({
      a: [cx + px * w, cy + py * w],
      b: [cx - px * w, cy - py * w],
      opacity: Math.max(0.2, Math.min(1, opacity)),
    });
  }
  return treads;
}

/** A closed polygon/hull path from a ring of points. */
function ringPath(points: readonly Pt[]): string {
  const pts = points.map(toSvg);
  if (pts.length === 0) return "";
  return `M ${pts.map((p) => `${p[0]} ${p[1]}`).join(" L ")} Z`;
}

/** The content bounding box over every draw command, in SVG (Y-down) space. */
function contentBounds(commands: readonly DrawCommand[], padFt: number) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const consider = (p: Pt) => {
    const [sx, sy] = toSvg(p);
    minX = Math.min(minX, sx);
    minY = Math.min(minY, sy);
    maxX = Math.max(maxX, sx);
    maxY = Math.max(maxY, sy);
  };
  for (const c of commands) {
    if (c.kind === "polygon" || c.kind === "hull" || c.kind === "edge" || c.kind === "channel" || c.kind === "backdrop") {
      for (const p of c.points) consider(p);
    } else if (c.kind === "barrier") {
      consider(c.at);
    }
    // gridlines + scalebar are informational overlays sized to the content; they
    // do not drive the content bounds (the grid already spans node+backdrop extent).
  }
  if (!Number.isFinite(minX)) {
    // Empty map — a small default frame so the SVG still has a viewBox.
    return { x: -50, y: -50, width: 100, height: 100 };
  }
  return {
    x: minX - padFt,
    y: minY - padFt,
    width: maxX - minX + padFt * 2,
    height: maxY - minY + padFt * 2,
  };
}

/** The glyph a barrier status draws (open door = gap, closed/locked = bar, destroyed = rubble). */
function barrierGlyph(status: string): { readonly symbol: string; readonly closed: boolean } {
  switch (status) {
    case "open":
      return { symbol: "▯", closed: false };
    case "destroyed":
      return { symbol: "⋯", closed: false };
    default:
      return { symbol: "▮", closed: true }; // closed/locked/barred/spiked
  }
}

/** The marker glyph for a found trap/secret/note. */
function markerGlyph(kind: "trap" | "secret" | "note"): string {
  return kind === "trap" ? "⚠" : kind === "secret" ? "✦" : "✎";
}

/**
 * Render the player-safe map as SVG. Pure: the same payload + seeds always
 * produce the same element tree (the jitter lives in the seeded draw plan). The
 * parent viewport applies zoom/pan via `transform`.
 */
export function MapCanvas({ map, currentNodeId, transform, ariaLabel = "Map", padFt = 20 }: MapCanvasProps): JSX.Element {
  // The draw plan is deterministic for the payload (seeded per node). RenderPayload
  // is structurally the wire SpatialMapGraph (same field names), so feed it directly.
  const commands = useMemo(() => buildDrawPlan(map as unknown as RenderPayload), [map]);
  const bounds = useMemo(() => contentBounds(commands, padFt), [commands, padFt]);

  // Node centers (SVG space) for marker + you-are-here placement.
  const centerOf = useMemo(() => {
    const m = new Map<string, Pt>();
    for (const n of map.nodes) m.set(n.nodeId, toSvg([n.coord.x, n.coord.y]));
    return m;
  }, [map]);

  // Each node's hierarchy tier, so the renderer can speak the right VOCABULARY
  // per scale (Map6 §13/§14): a MICRO node is a dungeon room/cavern; a MESO node
  // is a town building/plaza/intersection; a MACRO node a settlement/landmark.
  // Looked up by id here (not threaded through the draw plan) so micro stays
  // exactly as-is and meso gets a block/street treatment.
  const tierOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of map.nodes) m.set(n.nodeId, n.hierarchyLevel);
    return m;
  }, [map]);

  // Per-node FOG state, by the SAME rule the draw plan uses to set a command's
  // `masked` flag (`!visible || illumination === "darkness"`). The `channel`
  // draw command carries no `masked` field, so the floor-grid mask (R1) derives
  // a corridor's fog state from its incident rooms via this map + edgeEndpoints.
  const maskedOf = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const n of map.nodes) m.set(n.nodeId, !n.visible || n.illumination === "darkness");
    return m;
  }, [map]);

  /** An edge is a "street" when BOTH its endpoints are meso (town-scale) — Map6 §13. */
  const isStreet = (from: string, to: string): boolean =>
    tierOf.get(from) === "meso" && tierOf.get(to) === "meso";
  /** Resolve the from-node of an edge command by its `<from>__<to>` edgeId. */
  const edgeEndpoints = (edgeId: string): { from: string; to: string } => {
    const i = edgeId.indexOf("__");
    return i === -1 ? { from: "", to: "" } : { from: edgeId.slice(0, i), to: edgeId.slice(i + 2) };
  };

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      data-testid="map-canvas"
      viewBox={`${bounds.x} ${bounds.y} ${bounds.width} ${bounds.height}`}
      preserveAspectRatio="xMidYMid meet"
      className="map-canvas"
      // Fill the (now bounded) viewport box; preserveAspectRatio letterboxes the
      // content so a small map no longer scales up to the full column width.
      // `display:block` drops the inline-SVG baseline gap. No app stylesheet
      // exists, so the sizing is set here directly.
      style={{ width: "100%", height: "100%", display: "block" }}
    >
      {/* Reusable paint: the diagonal pencil-ish HATCH that fills the rock/wall
          mass. This is the SVG approximation of the hand-drawn hatching; the
          R11 pass swaps it for real Rough.js strokes. A unique id keeps two
          MapCanvas instances on one page from colliding is unnecessary here (the
          pattern is content-independent), so a stable id keeps determinism. */}
      <defs>
        {/* The rock/wall-mass hatch. Toned DOWN (R2): a lighter, lower-contrast
            stroke at reduced width + an explicit opacity ceiling so the 45°
            diagonal reads as subtle wall mass rather than prominent noise. Stays
            a single stable-id content-independent pattern (determinism). */}
        <pattern id="map-rock-hatch" patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
          <rect width={6} height={6} fill="#cbbca0" />
          <line x1={0} y1={0} x2={0} y2={6} stroke="#b8a884" strokeWidth={0.6} strokeOpacity={0.5} />
        </pattern>
        {/* FLOOR MASK (R1): the micro scale grid is painted through this mask so
            it shows ONLY on discovered, lit (unmasked) room + corridor FLOOR —
            never in the rock, never on an unseen floor. White = gridded floor,
            black = everything else. A <mask> (not a <clipPath>) is used so a
            STROKED curved corridor ribbon (fill:none Bezier) contributes its
            painted width — a clipPath clips by fill and would drop it. The shapes
            reuse the exact render helpers (ringPath/pointsAttr/bezierPath), so the
            mask lines up with the painted floors in the same Y-down user space. */}
        <mask
          id="map-floor-mask"
          maskUnits="userSpaceOnUse"
          x={bounds.x}
          y={bounds.y}
          width={bounds.width}
          height={bounds.height}
        >
          <rect x={bounds.x} y={bounds.y} width={bounds.width} height={bounds.height} fill="black" />
          {/* Room floors (unmasked only — a masked/darkness room is not gridded, R1.2). */}
          {commands.map((c, i) =>
            (c.kind === "polygon" || c.kind === "hull") && !c.masked ? (
              <path key={`fm-room-${i}`} d={ringPath(c.points)} fill="white" stroke="none" />
            ) : null,
          )}
          {/* Corridor floors: non-stub, non-vertical channels whose BOTH incident
              rooms are unmasked. Straight = filled quad; curved = stroked ribbon
              (both honored by a mask). The `!== false` check is fog-safe-by-default:
              an endpoint missing from maskedOf is NOT treated as a lit floor. */}
          {commands.map((c, i) => {
            if (c.kind !== "channel" || c.stub || c.vertical) return null;
            const { from, to } = edgeEndpoints(c.edgeId);
            if (maskedOf.get(from) !== false || maskedOf.get(to) !== false) return null;
            return c.curved ? (
              <path
                key={`fm-ch-${i}`}
                d={bezierPath(c.spine)}
                fill="none"
                stroke="white"
                strokeWidth={c.halfWidth * 2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : (
              <polygon key={`fm-ch-${i}`} points={pointsAttr(c.points)} fill="white" stroke="none" />
            );
          })}
        </mask>
      </defs>
      {/* The zoom/pan transform group (identity when no viewport wraps it). */}
      <g transform={transform} data-testid="map-transform-group">
        {/* LAYER 1 — ROCK. The whole frame is solid rock (hatched wall mass); the
            floor layers below carve rooms and corridors out of it. This is the
            inversion that makes an opening read as an opening: a doorway is where
            the rock is ABSENT, not a line drawn across a wall. */}
        <rect
          x={bounds.x}
          y={bounds.y}
          width={bounds.width}
          height={bounds.height}
          data-testid="map-rock"
          className="map-rock"
          fill="url(#map-rock-hatch)"
        />

        {/* NOTE: the micro SCALE GRID is no longer drawn here. It moved to render
            AFTER the room + corridor floor fills (below), clipped to the floor via
            `#map-floor-mask`, so the 5-ft ruler sits ON the floor like a real
            battle map instead of being painted over by the floor fills (R1). */}

        {/* LAYER 1c — DESCRIPTIVE BACKDROP (per tier): the ground/mass a room,
            building, or settlement sits in, under the node floors (R4). Keyed on
            the node's tier + shape (never DM-only placeKind), seeded independently
            of the node ring so it does not shift the room's own jitter. */}
        {commands.map((c, i) =>
          c.kind !== "backdrop" ? null : (
            <path
              key={`bd${i}`}
              d={ringPath(c.points)}
              data-backdrop-node-id={c.ofNodeId}
              data-backdrop-tier={c.tier}
              className={`map-backdrop map-backdrop-${c.tier}${c.masked ? " map-backdrop-masked" : ""}`}
              fill={c.tier === "macro" ? "#d9d2bb" : c.tier === "meso" ? "#d2c6a6" : "#e9e0c8"}
              fillOpacity={c.masked ? 0.4 : 0.7}
              stroke="none"
            />
          ),
        )}

        {/* LAYER 2 — ROOM FLOORS (carve-outs) with the heavy INKED wall edge.
            Painted OVER the rock so each room is a light floor punched into the
            mass, outlined by the thick dark line that reads as the room's wall. */}
        {commands.map((c, i) => {
          if (c.kind !== "polygon" && c.kind !== "hull") return null;
          const isCurrent = c.nodeId === currentNodeId;
          // A MESO node is a town building/plaza/intersection, not a dungeon room:
          // draw it as a filled block (a solid footprint) so a town reads as
          // buildings along streets rather than hollow rooms (Map6 §13). Micro
          // (and macro, for now) keep the carved-room floor look.
          const meso = tierOf.get(c.nodeId) === "meso";
          const fill = c.masked
            ? "#cbbfa6"
            : meso
              ? "#d8c7a0" // a filled building block
              : "#f6efdd"; // a carved dungeon-room floor (parchment)
          const fillOpacity = c.masked ? 0.55 : 1;
          return (
            <path
              key={`n${i}`}
              d={ringPath(c.points)}
              data-node-id={c.nodeId}
              data-node-tier={tierOf.get(c.nodeId) ?? "micro"}
              data-masked={c.masked ? "true" : "false"}
              data-current={isCurrent ? "true" : "false"}
              className={`map-node${c.masked ? " map-node-masked" : ""}${isCurrent ? " map-node-current" : ""}${meso ? " map-node-meso" : ""}`}
              fill={fill}
              fillOpacity={fillOpacity}
              stroke={isCurrent ? "#c0392b" : "#2b2016"}
              strokeWidth={isCurrent ? 3 : meso ? 2 : 2.25}
              strokeLinejoin="round"
            />
          );
        })}

        {/* LAYER 3 — CORRIDOR FLOOR CHANNELS. The walkable strip each corridor
            carves through the rock, painted OVER the room ink edges. Because the
            channel floor overlaps 2ft into each room (draw-plan), its parchment
            fill ERASES the room's wall line exactly at the doorway — so the
            opening is unlined and the two rooms' floors visibly join through the
            passage. The rock hatch flanking the channel's long sides forms the
            corridor walls. A destroyed/collapsed passage is drawn rubble-dashed. */}
        {commands.map((c, i) => {
          if (c.kind !== "channel") return null;
          const { from, to } = edgeEndpoints(c.edgeId);
          const street = isStreet(from, to);
          // Floor color matches the rooms it joins (town street vs dungeon floor).
          const floor = c.destroyed ? "#e7d9bf" : street ? "#e4d7b4" : "#f6efdd";
          const cls = `map-channel${c.destroyed ? " map-channel-destroyed" : ""}${street ? " map-channel-street" : ""}${c.stub ? " map-channel-stub" : ""}${c.vertical ? " map-channel-vertical" : ""}`;
          // A VERTICAL connection (stairs) — a self-contained landing stamped on
          // the room wall with transverse STAIR TREADS, so it reads as "steps up/
          // down a level", never as a diagonal corridor. The up/down badge is a
          // separate marker pass below. Treads: evenly-spaced short segments
          // perpendicular to the landing spine; for DOWN they taper + darken into
          // shadow, for UP they stay full-width (receding into the ceiling).
          if (c.vertical) {
            const treads = stairTreads(c.spine, c.halfWidth, c.vertical);
            return (
              <g key={`ch${i}`} data-channel-edge-id={c.edgeId} data-channel-vertical={c.vertical} className={cls}>
                <polygon points={pointsAttr(c.points)} fill={floor} stroke="#6b5a3e" strokeWidth={0.9} strokeLinejoin="round" />
                {treads.map((seg, ti) => (
                  <line
                    key={`tread${i}-${ti}`}
                    x1={toSvg(seg.a)[0]}
                    y1={toSvg(seg.a)[1]}
                    x2={toSvg(seg.b)[0]}
                    y2={toSvg(seg.b)[1]}
                    stroke="#2b2016"
                    strokeWidth={1.2}
                    strokeOpacity={seg.opacity}
                    strokeLinecap="round"
                  />
                ))}
              </g>
            );
          }
          // A STUB passage runs off into unexplored rock: a half-faded floor so
          // it dissolves at the open mouth, outlined with a dashed "the corridor
          // continues" edge. We show the opening, never what is beyond (R4/R11.6).
          if (c.stub) {
            return (
              <polygon
                key={`ch${i}`}
                points={pointsAttr(c.points)}
                data-channel-edge-id={c.edgeId}
                data-channel-stub="true"
                className={cls}
                fill={floor}
                fillOpacity={0.5}
                stroke="#6b5a3e"
                strokeWidth={0.9}
                strokeDasharray="3 2.5"
                strokeLinejoin="round"
              />
            );
          }
          // A straight channel is its quad; a curved one sweeps a thick ribbon
          // along the Bezier spine (round caps/joins so the floor is continuous).
          return c.curved ? (
            <path
              key={`ch${i}`}
              d={bezierPath(c.spine)}
              data-channel-edge-id={c.edgeId}
              className={cls}
              fill="none"
              stroke={floor}
              strokeWidth={c.halfWidth * 2}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={c.destroyed ? "5 4" : undefined}
            />
          ) : (
            <polygon
              key={`ch${i}`}
              points={pointsAttr(c.points)}
              data-channel-edge-id={c.edgeId}
              className={cls}
              fill={floor}
              fillOpacity={c.destroyed ? 0.6 : 1}
              stroke="none"
            />
          );
        })}

        {/* LAYER 3b — SCALE GRID LINES (micro only), on the FLOOR (R1). Rendered
            AFTER the room + corridor floor fills, clipped via `#map-floor-mask` to
            the discovered, lit floor regions, so the 5-ft (or viewport-adapted)
            ruler appears INSIDE explorable spaces and nowhere in the rock. A pure
            reference ruler aligned to the coordinate origin — never tactical
            occupancy (R5/boundary guard). Meso/macro emit a scale legend (below),
            never grid lines, so this branch only fires for the micro `gridlines`
            command. The grid line segments are unchanged from the draw plan — only
            the render site (now post-floor) and the floor mask are new. */}
        {commands.map((c, i) =>
          c.kind !== "gridlines" ? null : (
            <g
              key={`grid${i}`}
              data-testid="map-gridlines"
              data-grid-interval={c.intervalFt}
              className="map-gridlines"
              mask="url(#map-floor-mask)"
            >
              {c.lines.map((ln, j) => {
                const [x1, y1] = toSvg(ln.from);
                const [x2, y2] = toSvg(ln.to);
                return (
                  <line
                    key={`gl${i}-${j}`}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke="#8c7a5c"
                    strokeWidth={0.4}
                    strokeOpacity={0.35}
                  />
                );
              })}
            </g>
          ),
        )}

        {/* STUB "continues" marker — a small ellipsis at each unexplored mouth so
            the open end reads as "the passage goes on" rather than a dead-end
            room. Placed at the far end of the stub spine (into the rock). */}
        {commands.map((c, i) => {
          if (c.kind !== "channel" || !c.stub) return null;
          const far = c.spine[c.spine.length - 1]!;
          const [fx, fy] = toSvg(far);
          return (
            <text
              key={`stub${i}`}
              x={fx}
              y={fy}
              data-stub-marker-edge-id={c.edgeId}
              className="map-stub-marker"
              fontSize={9}
              fill="#6b5a3e"
              fillOpacity={0.8}
              textAnchor="middle"
              dominantBaseline="central"
            >
              <title>The passage continues (unexplored)</title>
              ⋯
            </text>
          );
        })}

        {/* VERTICAL "UP/DN" badge — a small arrowed label at the far end of each
            stair landing so the player instantly reads which way the steps go
            (and the DM/tests can key on it). Pairs with the tread stamp above. */}
        {commands.map((c, i) => {
          if (c.kind !== "channel" || !c.vertical) return null;
          const far = c.spine[c.spine.length - 1]!;
          const [fx, fy] = toSvg(far);
          return (
            <text
              key={`vmark${i}`}
              x={fx}
              y={fy}
              data-vertical-marker-edge-id={c.edgeId}
              data-vertical-direction={c.vertical}
              className="map-vertical-marker"
              fontSize={7}
              fill="#5a4632"
              textAnchor="middle"
              dominantBaseline="central"
            >
              <title>{c.vertical === "up" ? "Stairs up (another level)" : "Stairs down (another level)"}</title>
              {c.vertical === "up" ? "▲ UP" : "▼ DN"}
            </text>
          );
        })}

        {/* EGRESS "EXIT" badge — the site's WAY OUT to the world (surface-egress addendum).
            A labelled marker at the far (outward) end of the egress channel so the player
            reads "this is the way out" and its FORM, distinct from an interior up/down
            stair. Glyph + label vary by the DM-declared exitKind. */}
        {commands.map((c, i) => {
          if (c.kind !== "channel" || !c.egress) return null;
          const far = c.spine[c.spine.length - 1]!;
          const [fx, fy] = toSvg(far);
          const label =
            c.egress === "stair_up" || c.egress === "trapdoor"
              ? "▲ EXIT"
              : c.egress === "portal"
                ? "◎ EXIT"
                : "⤴ EXIT";
          const title =
            c.egress === "stair_up"
              ? "Stairs up and out of the site"
              : c.egress === "trapdoor"
                ? "A trapdoor up and out"
                : c.egress === "door_at_grade"
                  ? "A door out to ground level"
                  : c.egress === "cave_mouth"
                    ? "A cave mouth to the outside"
                    : c.egress === "ramp"
                      ? "A ramp out of the site"
                      : c.egress === "portal"
                        ? "A portal out of the site"
                        : "The way out of the site";
          return (
            <text
              key={`egress${i}`}
              x={fx}
              y={fy}
              data-egress-marker-edge-id={c.edgeId}
              data-egress-kind={c.egress}
              className="map-egress-marker"
              fontSize={7}
              fill="#5a4632"
              textAnchor="middle"
              dominantBaseline="central"
            >
              <title>{title}</title>
              {label}
            </text>
          );
        })}

        {/* LAYER 4 — EDGE CENTERLINES. Kept as a faint thread down the corridor
            (and the street styling for towns) so the connection's identity +
            `data-edge-id` persist for tests/overlays. Thin and under the glyphs;
            the channel floor already carries the visual passage. */}
        {commands.map((c, i) => {
          if (c.kind !== "edge") return null;
          const { from, to } = edgeEndpoints(c.edgeId);
          const street = isStreet(from, to);
          const common = {
            className: `map-edge${c.destroyed ? " map-edge-destroyed" : ""}${street ? " map-edge-street" : ""}`,
            stroke: c.destroyed ? "#b08968" : street ? "#9a8c6e" : "#9b8a76",
            strokeWidth: street ? 3 : 0.75,
            strokeDasharray: c.destroyed ? "4 3" : undefined,
            strokeLinecap: "round" as const,
            strokeOpacity: street ? 0.9 : 0.5,
            fill: "none",
          };
          return c.curved ? (
            <path key={`e${i}`} d={bezierPath(c.points)} data-edge-id={c.edgeId} {...common} />
          ) : (
            <polyline key={`e${i}`} points={pointsAttr(c.points)} data-edge-id={c.edgeId} {...common} />
          );
        })}
        {/* BARRIER glyphs on edges. */}
        {commands.map((c, i) => {
          if (c.kind !== "barrier") return null;
          // Flip to SVG Y-down space like every other element (the draw plan
          // emits `at` in map Y-up coords) so the glyph sits on its edge and
          // inside the computed content bounds.
          const [bx, by] = toSvg(c.at);
          const glyph = barrierGlyph(c.status);
          return (
            <text
              key={`b${i}`}
              x={bx}
              y={by}
              data-barrier-edge-id={c.edgeId}
              data-barrier-status={c.status}
              className={`map-barrier${glyph.closed ? " map-barrier-closed" : ""}`}
              // Larger than a corridor glyph so a door reads clearly as an opening
              // ON its wall (the draw plan now places `at` on the from-wall).
              fontSize={11}
              fill={glyph.closed ? "#7a3b2e" : "#5a4632"}
              textAnchor="middle"
              dominantBaseline="central"
            >
              <title>{`${c.barrierType} (${c.status})`}</title>
              {glyph.symbol}
            </text>
          );
        })}
        {/* FOUND trap/secret markers (from the payload — never an unfound one). */}
        {map.nodes.flatMap((n) =>
          (n.markers ?? []).map((mk, j) => {
            const c = centerOf.get(n.nodeId);
            if (!c) return null;
            return (
              <text
                key={`m${n.nodeId}-${j}`}
                x={c[0]}
                y={c[1] - 6}
                data-marker-kind={mk.kind}
                data-marker-node-id={n.nodeId}
                className={`map-marker map-marker-${mk.kind}`}
                fontSize={8}
                textAnchor="middle"
                dominantBaseline="central"
              >
                <title>{mk.label}</title>
                {markerGlyph(mk.kind)}
              </text>
            );
          }),
        )}
        {/* OCCUPANT dots: entities the server deemed VISIBLE to this character
            (fog-of-war already withheld the rest). Scattered deterministically
            inside the room via the node's renderSeed so "something is in here"
            reads without the dots stacking on the center. A dot is a neutral
            presence marker — the payload types entities only by id, so we never
            fabricate what KIND of thing it is (R11.6: render only what we were
            told). The party's own current node is skipped (the you-are-here
            marker covers it). */}
        {map.nodes.flatMap((n) => {
          if (n.nodeId === currentNodeId) return [];
          const c = centerOf.get(n.nodeId);
          const ents = n.entities ?? [];
          if (!c || ents.length === 0) return [];
          const rng = new SeededRandom(n.renderSeed ^ 0x5f3759df);
          const spread = Math.max(4, Math.min((n.dims?.widthFt ?? 20) / 3, 14));
          // Scatter in a STABLE order (by entityId) so the dots don't jump if the
          // server returns the same entities in a different array order between
          // payloads — the placement stays reproducible per renderSeed (R11.2).
          const ordered = [...ents].sort((p, q) => (p.entityId < q.entityId ? -1 : p.entityId > q.entityId ? 1 : 0));
          return ordered.map((e, k) => {
            const dx = (rng.next() - 0.5) * 2 * spread;
            const dy = (rng.next() - 0.5) * 2 * spread;
            return (
              <circle
                key={`ent-${n.nodeId}-${e.entityId}-${k}`}
                cx={c[0] + dx}
                cy={c[1] + dy}
                r={2}
                data-entity-id={e.entityId}
                className="map-entity-dot"
                fill="#3a2d1a"
                fillOpacity={0.75}
              />
            );
          });
        })}
        {/* "YOU ARE HERE" marker on the party's current node. */}
        {currentNodeId && centerOf.has(currentNodeId) ? (
          <g data-testid="you-are-here" transform={`translate(${centerOf.get(currentNodeId)![0]} ${centerOf.get(currentNodeId)![1]})`}>
            <circle r={3.5} className="map-here-dot" fill="#c0392b" stroke="#fff" strokeWidth={1} />
          </g>
        ) : null}

        {/* SCALE LEGEND (meso/macro only): a labeled bar reading real-world
            distance, since a town/region map shows scale by a legend rather than
            grid squares (R5.3). Drawn on top so it stays readable; purely
            informational (R5.6). The bar runs `lengthFt` map-feet east from `at`. */}
        {commands.map((c, i) => {
          if (c.kind !== "scalebar") return null;
          const [ax, ay] = toSvg(c.at);
          const [bx] = toSvg([c.at[0] + c.lengthFt, c.at[1]]);
          return (
            <g key={`scale${i}`} data-testid="map-scalebar" data-scale-length-ft={c.lengthFt} data-scale-label={c.label} className="map-scalebar">
              <line x1={ax} y1={ay} x2={bx} y2={ay} stroke="#2b2016" strokeWidth={1.4} strokeLinecap="round" />
              <line x1={ax} y1={ay - 3} x2={ax} y2={ay + 3} stroke="#2b2016" strokeWidth={1.4} />
              <line x1={bx} y1={ay - 3} x2={bx} y2={ay + 3} stroke="#2b2016" strokeWidth={1.4} />
              <text x={(ax + bx) / 2} y={ay - 5} fontSize={8} fill="#2b2016" textAnchor="middle">
                {c.label}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
}
